import { useEffect, useState } from "react";
import { imageUrl } from "@/lib/api";
import { useI18n } from "@/context/LangContext";
import { pickLocalized, labelType, labelCondition } from "@/lib/i18n";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  MapPin,
  Calendar,
  Buildings,
  Shield,
  CaretLeft,
  CaretRight,
  X,
  PencilSimple,
  Trash,
} from "@phosphor-icons/react";

export default function HeritageDetail({ item, open, onOpenChange, user, onEdit, onDelete }) {
  const { lang, t } = useI18n();
  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState(false);

  const canEdit = !!(item && user);

  const images = (item?.image_urls && item.image_urls.length > 0)
    ? item.image_urls
    : item?.image_url
    ? [item.image_url]
    : [];

  useEffect(() => {
    setActive(0);
  }, [item]);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(false);
      if (e.key === "ArrowRight") setActive((i) => (i + 1) % images.length);
      if (e.key === "ArrowLeft") setActive((i) => (i - 1 + images.length) % images.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, images.length]);

  if (!item) return null;

  const name = pickLocalized(item, "name", lang);
  const description = pickLocalized(item, "description", lang);
  const significance = pickLocalized(item, "historical_significance", lang);
  const location = pickLocalized(item, "location", lang);
  const managed = pickLocalized(item, "managed_by", lang);
  const owner = pickLocalized(item, "owner", lang);

  const next = () => setActive((i) => (i + 1) % images.length);
  const prev = () => setActive((i) => (i - 1 + images.length) % images.length);
  const cover = images[active];

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="max-w-3xl bg-white border border-[#e5e3db] rounded-none p-0 overflow-hidden max-h-[90vh] overflow-y-auto"
          data-testid="heritage-detail-dialog"
        >
          <div className="relative aspect-[21/9] bg-[#efece4]">
            {cover ? (
              <button
                type="button"
                onClick={() => setLightbox(true)}
                className="absolute inset-0 w-full h-full block"
                data-testid="detail-open-lightbox"
                aria-label="Open lightbox"
              >
                <img src={imageUrl(cover)} alt={name} className="w-full h-full object-cover" />
              </button>
            ) : (
              <div className="w-full h-full flex items-center justify-center text-[#b7b0a0] serif italic">
                No image
              </div>
            )}
            <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-black/45 via-black/10 to-transparent" />

            {images.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); prev(); }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 h-9 w-9 bg-white/90 border border-white/70 text-[#1a1a1a] flex items-center justify-center hover:bg-white transition-colors"
                  aria-label="Previous image"
                  data-testid="detail-prev-btn"
                >
                  <CaretLeft size={16} weight="bold" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); next(); }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 h-9 w-9 bg-white/90 border border-white/70 text-[#1a1a1a] flex items-center justify-center hover:bg-white transition-colors"
                  aria-label="Next image"
                  data-testid="detail-next-btn"
                >
                  <CaretRight size={16} weight="bold" />
                </button>
                <div className="absolute top-4 right-4 bg-black/60 text-white text-[10px] uppercase tracking-[0.2em] font-semibold px-2 py-1">
                  {active + 1} / {images.length}
                </div>
              </>
            )}

            <div className="absolute bottom-6 left-8 right-8 text-white pointer-events-none">
              <div className="text-[10px] uppercase tracking-[0.28em] font-semibold opacity-90">
                {labelType(item.type, lang)} · {item.local_authority}
              </div>
              <DialogTitle className="serif text-3xl sm:text-4xl font-bold mt-1 drop-shadow-sm">
                {name}
              </DialogTitle>
            </div>
          </div>

          {images.length > 1 && (
            <div className="px-8 pt-4 flex gap-2 overflow-x-auto" data-testid="detail-thumbnails">
              {images.map((u, i) => (
                <button
                  key={u + i}
                  onClick={() => setActive(i)}
                  className={`flex-shrink-0 w-20 h-16 border overflow-hidden transition-opacity ${
                    active === i
                      ? "border-[#b25a38] opacity-100"
                      : "border-[#e5e3db] opacity-70 hover:opacity-100"
                  }`}
                  data-testid={`detail-thumb-${i}`}
                >
                  <img src={imageUrl(u)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}

          <div className="p-8 space-y-6">
            <DialogHeader className="sr-only">
              <DialogTitle>{name}</DialogTitle>
              <DialogDescription>Heritage record detail</DialogDescription>
            </DialogHeader>

            {canEdit && (
              <div className="flex items-center justify-end gap-2 -mb-2" data-testid="detail-actions">
                <button
                  type="button"
                  onClick={() => {
                    onOpenChange(false);
                    onEdit?.(item);
                  }}
                  className="inline-flex items-center gap-2 h-9 px-4 border border-[#e5e3db] bg-white text-xs uppercase tracking-[0.18em] font-semibold text-[#1a1a1a] hover:bg-[#1a1a1a] hover:text-white hover:border-[#1a1a1a] transition-colors"
                  data-testid="detail-edit-btn"
                >
                  <PencilSimple size={14} weight="duotone" />
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => {
                    onOpenChange(false);
                    onDelete?.(item);
                  }}
                  className="inline-flex items-center gap-2 h-9 px-4 border border-[#e5e3db] bg-white text-xs uppercase tracking-[0.18em] font-semibold text-[#d32f2f] hover:bg-[#d32f2f] hover:text-white hover:border-[#d32f2f] transition-colors"
                  data-testid="detail-delete-btn"
                >
                  <Trash size={14} weight="duotone" />
                  Delete
                </button>
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 border-y border-[#e5e3db] py-5">
              <Stat icon={<MapPin size={16} weight="duotone" />} label={t("detail.location")} value={location || "—"} />
              <Stat icon={<Calendar size={16} weight="duotone" />} label={t("detail.year")} value={item.year_built || "—"} />
              <Stat icon={<Shield size={16} weight="duotone" />} label={t("detail.condition")} value={labelCondition(item.condition, lang)} />
              <Stat icon={<Buildings size={16} weight="duotone" />} label={t("detail.owner")} value={owner || "—"} />
              <Stat icon={<Buildings size={16} weight="duotone" />} label={t("detail.managed")} value={managed || "—"} />
              <Stat icon={<Shield size={16} weight="duotone" />} label={t("detail.status")} value={item.status ? t(`status.${item.status}`) : "—"} />
              {item.status === "Gazetted" && item.gazette_no && (
                <Stat icon={<Shield size={16} weight="duotone" />} label={t("detail.gazette_no")} value={item.gazette_no} />
              )}
              {item.land_status && (
                <Stat icon={<MapPin size={16} weight="duotone" />} label={t("detail.land_status")} value={item.land_status} />
              )}
            </div>

            {description && (
              <section>
                <div className="overline mb-2">{t("detail.description")}</div>
                <p className="text-sm text-[#1a1a1a] leading-relaxed">{description}</p>
              </section>
            )}

            {significance && (
              <section>
                <div className="overline mb-2">{t("detail.significance")}</div>
                <p className="text-sm text-[#1a1a1a] leading-relaxed">{significance}</p>
              </section>
            )}

            <section className="pt-4 border-t border-[#e5e3db] grid grid-cols-2 gap-4 text-xs text-[#5c5c5c]">
              <div>
                <div className="overline mb-1">{t("detail.latitude")}</div>
                <div className="font-mono text-[#1a1a1a]">{item.latitude?.toFixed(6)}</div>
              </div>
              <div>
                <div className="overline mb-1">{t("detail.longitude")}</div>
                <div className="font-mono text-[#1a1a1a]">{item.longitude?.toFixed(6)}</div>
              </div>
            </section>
          </div>
        </DialogContent>
      </Dialog>

      {lightbox && cover && (
        <div
          className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center"
          data-testid="lightbox-overlay"
          onClick={() => setLightbox(false)}
        >
          <button
            onClick={() => setLightbox(false)}
            className="absolute top-5 right-5 h-10 w-10 bg-white/10 hover:bg-white/20 text-white flex items-center justify-center border border-white/20"
            aria-label="Close lightbox"
            data-testid="lightbox-close"
          >
            <X size={18} weight="bold" />
          </button>
          <div className="absolute top-5 left-5 text-white text-xs uppercase tracking-[0.24em] font-semibold">
            {name} · {active + 1} / {images.length}
          </div>
          {images.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); prev(); }}
                className="absolute left-6 top-1/2 -translate-y-1/2 h-12 w-12 bg-white/10 hover:bg-white/20 text-white flex items-center justify-center border border-white/20"
                aria-label="Previous"
                data-testid="lightbox-prev"
              >
                <CaretLeft size={20} weight="bold" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); next(); }}
                className="absolute right-6 top-1/2 -translate-y-1/2 h-12 w-12 bg-white/10 hover:bg-white/20 text-white flex items-center justify-center border border-white/20"
                aria-label="Next"
                data-testid="lightbox-next"
              >
                <CaretRight size={20} weight="bold" />
              </button>
            </>
          )}
          <img
            src={imageUrl(cover)}
            alt={name}
            className="max-h-[85vh] max-w-[90vw] object-contain shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[#b25a38] mb-1">{icon}
        <span className="overline text-[#5c5c5c]">{label}</span>
      </div>
      <div className="text-sm text-[#1a1a1a] font-medium truncate">{value}</div>
    </div>
  );
}
